import {
  NotificationService,
  OuiButtonComponent,
  OuiTextComponent,
  OuiTooltipComponent,
  SaveService,
} from '@aiz-gl/omni-ui';
import { HttpResponse } from '@angular/common/http';
import { Component, inject, input, signal } from '@angular/core';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-download-button',
  imports: [OuiTooltipComponent, OuiButtonComponent, OuiTextComponent],
  templateUrl: './download-button.component.html',
  standalone: true,
})
export class DownloadButtonComponent {
  private save = inject(SaveService);
  private notification = inject(NotificationService);

  mode = input<'text' | 'icon'>('icon');
  label = input<string>('Download');
  tooltipPlacement = input<'right' | 'left' | 'top'>('top');
  downloadFileName = input<string>('');
  disable = input<boolean>(false);
  size = input<'sm' | 'md' | 'lg'>('md');
  noContentMessage = input<string>('No content to download.');
  preserveFileExtension = input<boolean>(false);
  preferFileNameFromHeader = input<boolean>(false);

  blobObservable = input<Observable<HttpResponse<Blob>>>();

  downloading = signal<boolean>(false);

  download() {
    const fallbackFileName = this.downloadFileName();
    const blobObservable = this.blobObservable();
    if (!blobObservable) return;
    this.downloading.set(true);
    blobObservable.subscribe({
      next: (response) => {
        if (response.status === 204) {
          this.notification.showAlert({
            symbolName: 'file_download_off',
            message: this.noContentMessage(),
            variant: 'warn',
          });
        }

        const blob = response.body;
        if (blob) {
          const fileNameFromHeader = this.preferFileNameFromHeader()
            ? this._getFileNameFromHeaders(response)
            : undefined;
          const resolvedFileName = fileNameFromHeader || fallbackFileName;
          const sanitizedFileName = this._sanitizeFilename(
            resolvedFileName,
            this.preserveFileExtension(),
          );
          this.save.saveAs(blob, sanitizedFileName);
        }
        this.downloading.set(false);
      },
    });
  }

  private _getFileNameFromHeaders(response: HttpResponse<Blob>) {
    const contentDisposition = response.headers.get('content-disposition');
    if (!contentDisposition) return undefined;

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);

    const standardMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return standardMatch?.[1];
  }

  private _sanitizeFilename(filename: string, preserveFileExtension = false) {
    // Replace reserved characters, periods, and spaces with an underscore
    // The \s matches any whitespace character (space, tab, newline, etc.)
    // The \. matches a literal period (escaped because . has special meaning in regex)
    const filenameRegex = preserveFileExtension
      ? /[/\\?%*:|"<>\s]/g
      : /[/\\?%*:|"<>.\s]/g;
    filename = filename.replace(filenameRegex, '_');

    // Remove control characters (e.g., replace with empty string or another character)
    // eslint-disable-next-line no-control-regex
    filename = filename.replace(/[\x00-\x1F\x80-\x9F]/g, '');

    // Handle special cases like Windows reserved filenames (optional, but good practice)
    const windowsReserved = /^(con|prn|aux|nul|com[0-9]|lpt[0-9])$/i;
    if (windowsReserved.test(filename)) {
      filename = '_' + filename; // Prefix to avoid collision
    }

    // Remove leading/trailing periods and spaces (especially for Windows compatibility)
    filename = filename.replace(/^\.|\.$/g, '');
    filename = filename.replace('__', '_');
    filename = filename.trim();

    // You might also want to truncate the filename to a certain length to prevent issues with maximum path lengths
    // const maxLength = 255;
    // filename = filename.substring(0, maxLength);

    return filename;
  }
}
